/**
 * Front-end error tracking (Sprint 36).
 *
 * Mirrors the backend's opt-in model: with no `VITE_SENTRY_DSN` (dev/CI),
 * `initSentry()` is a no-op and nothing is loaded or sent. When the DSN is set
 * at build time, render crashes (via the ErrorBoundary) and unhandled errors
 * report to Sentry.
 *
 * PII discipline: `sendDefaultPii: false` plus a `beforeSend` that redacts
 * Authorization/Cookie headers — the user-facing fallback never names Sentry.
 */
import * as Sentry from '@sentry/react'

const _SENSITIVE_HEADERS = ['authorization', 'cookie', 'set-cookie', 'x-api-key']

export function initSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return false

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend(event) {
      const headers = event.request?.headers
      if (headers) {
        for (const key of Object.keys(headers)) {
          if (_SENSITIVE_HEADERS.includes(key.toLowerCase())) {
            headers[key] = '[Filtered]'
          }
        }
      }
      return event
    },
  })
  return true
}

/**
 * Report a caught error. Safe to call when Sentry is disabled — the SDK
 * no-ops `captureException` until `init` runs, so callers needn't gate on DSN.
 */
export function captureError(error: unknown): void {
  Sentry.captureException(error)
}
